const QUOTA_ERROR_MESSAGES = {
  PLAN_REQUIRED: 'Votre abonnement ne permet pas cette action.',
  PRODUCT_QUOTA_EXCEEDED:
    'La limite de produits de votre abonnement est atteinte.',
  PROJECT_QUOTA_EXCEEDED:
    'La limite de projets de votre abonnement est atteinte.',
  QUOTA_EXCEEDED: 'La limite de votre abonnement est atteinte.',
  SEAT_LIMIT_EXCEEDED: 'La limite de membres de votre abonnement est atteinte.',
  SEAT_GRACE_LIMIT_REACHED:
    "Tous les accès temporaires sont utilisés. L'invitation reste en attente jusqu'à l'ajout d'un siège.",
  SEAT_GRACE_ALREADY_USED:
    "Cette personne a déjà utilisé sa période de grâce. L'invitation reste en attente jusqu'à l'ajout d'un siège.",
  SEAT_BILLING_UNAVAILABLE:
    "Impossible d'activer ce membre pour le moment. Demandez à un administrateur de vérifier la facturation, puis réessayez. L'invitation reste en attente.",
  SIGNATURE_QUOTA_EXCEEDED:
    'La limite de signatures de devis de votre abonnement est atteinte.',
} as const

type QuotaErrorCode = keyof typeof QUOTA_ERROR_MESSAGES

const QUOTA_ERROR_TRANSLATION_KEYS = {
  PLAN_REQUIRED: 'quota_plan_required',
  PRODUCT_QUOTA_EXCEEDED: 'quota_product_exceeded',
  PROJECT_QUOTA_EXCEEDED: 'quota_project_exceeded',
  QUOTA_EXCEEDED: 'quota_exceeded',
  SEAT_LIMIT_EXCEEDED: 'quota_seat_exceeded',
  SEAT_GRACE_LIMIT_REACHED: 'quota_seat_grace_limit_reached',
  SEAT_GRACE_ALREADY_USED: 'quota_seat_grace_already_used',
  SEAT_BILLING_UNAVAILABLE: 'quota_seat_billing_unavailable',
  SIGNATURE_QUOTA_EXCEEDED: 'quota_signature_exceeded',
} satisfies Record<QuotaErrorCode, string>

type GraphqlErrorLike = {
  errorType?: unknown
  message?: unknown
  extensions?: {
    code?: unknown
    errorType?: unknown
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value != null && typeof value === 'object'
}

function normalizeErrorCode(value: unknown): string | null {
  if (typeof value !== 'string') return null
  const code = value.trim()
  return code ? code : null
}

function findMappedQuotaCode(candidates: unknown[]): QuotaErrorCode | null {
  for (const candidate of candidates) {
    const code = normalizeErrorCode(candidate)
    if (
      code &&
      Object.prototype.hasOwnProperty.call(QUOTA_ERROR_MESSAGES, code)
    ) {
      return code as QuotaErrorCode
    }
  }

  return null
}

function readGraphqlErrors(error: unknown): GraphqlErrorLike[] {
  if (!isRecord(error)) return []

  const errors = error.errors
  if (Array.isArray(errors)) return errors.filter(isRecord)

  const cause = error.cause
  if (isRecord(cause) && Array.isArray(cause.errors)) {
    return cause.errors.filter(isRecord)
  }

  return []
}

function readHttpErrorPayload(error: unknown): Record<string, unknown> | null {
  if (!isRecord(error)) return null

  const response = error.response
  if (!isRecord(response)) return null

  const data = response.data
  return isRecord(data) ? data : null
}

function readQuotaCodeFromGraphqlError(
  error: GraphqlErrorLike,
): QuotaErrorCode | null {
  return findMappedQuotaCode([
    error.extensions?.code,
    error.extensions?.errorType,
    error.errorType,
    error.message,
  ])
}

export function getQuotaErrorMessage(error: unknown): string | null {
  const code = getQuotaErrorCode(error)
  return code ? (QUOTA_ERROR_MESSAGES[code] ?? null) : null
}

export function getQuotaErrorMessageKey(error: unknown): string | null {
  const code = getQuotaErrorCode(error)
  return code ? (QUOTA_ERROR_TRANSLATION_KEYS[code] ?? null) : null
}

export function getQuotaErrorCode(error: unknown): QuotaErrorCode | null {
  const directCode = isRecord(error)
    ? findMappedQuotaCode([error.name, error.code, error.message])
    : null
  if (directCode) return directCode

  const httpPayload = readHttpErrorPayload(error)
  const httpCode = httpPayload
    ? findMappedQuotaCode([
        httpPayload.code,
        httpPayload.error,
        httpPayload.errorType,
      ])
    : null
  if (httpCode) return httpCode

  for (const graphqlError of readGraphqlErrors(error)) {
    const code = readQuotaCodeFromGraphqlError(graphqlError)
    if (code) return code
  }

  return null
}
